import React, { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { formatDate } from "../../utils/dateFormatting";
import { getBotIcon } from "../../utils/botFormatting";
import { useNavigateWithCommandClick } from "../../hooks/useNavigateWithCommandClick";

import Button from "../../components/Button";
import { Table, Row, Column, ActionsColumn } from "../../components/Table";
import {
  LoadingState,
  EmptyState,
  ErrorState
} from "../../components/CommonStates";
import Modal, {
  ModalTitle,
  ModalDescription,
  ModalActions,
  BotList,
  BotItem,
  BotCheckbox,
  BotLabel,
  BotIcon
} from "../../components/Modal";
import { Input } from "../../components/Input";
import { Header } from "../../components/AdminTab";

function DomainRulesTab({ onError, onSuccess, availableBots }) {
  const [domainRules, setDomainRules] = useState([]);
  const [domainRulesLoading, setDomainRulesLoading] = useState(false);
  const [domainRulesError, setDomainRulesError] = useState("");
  const [domainRuleModal, setDomainRuleModal] = useState(null);
  const [domainRuleFormData, setDomainRuleFormData] = useState({
    domain: "",
    allowedBots: []
  });
  const [savingDomainRule, setSavingDomainRule] = useState(false);
  const [deletingDomainRules, setDeletingDomainRules] = useState(new Set());
  const [domainRuleDeleteConfirmation, setDomainRuleDeleteConfirmation] =
    useState(null);
  const navigate = useNavigateWithCommandClick();

  const fetchDomainRules = async () => {
    setDomainRulesLoading(true);
    setDomainRulesError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.adminDomains, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();

      if (response.ok) {
        setDomainRules(data.domains || []);
      } else {
        setDomainRulesError(data.error || "Failed to fetch domain rules");
      }
    } catch (error) {
      setDomainRulesError("Network error. Please try again.");
    } finally {
      setDomainRulesLoading(false);
    }
  };

  const createDomainRule = async (domainData) => {
    setSavingDomainRule(true);
    setDomainRulesError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.createDomainWithBots, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          domain: domainData.domain,
          allowedBots: domainData.allowedBots
        })
      });

      const data = await response.json();

      if (response.ok) {
        await fetchDomainRules();
        onSuccess(data.message || "Domain rule created successfully");
        setDomainRuleModal(null);
        setDomainRuleFormData({ domain: "", allowedBots: [] });
      } else {
        setDomainRulesError(data.error || "Failed to create domain rule");
      }
    } catch (error) {
      setDomainRulesError("Network error. Please try again.");
    } finally {
      setSavingDomainRule(false);
    }
  };

  const addBotsToDomain = async (domainId, botIds) => {
    const token = localStorage.getItem("token");
    const response = await fetch(API_ENDPOINTS.addDomainBots(domainId), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ botIds })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || "Failed to add bots to domain");
    }

    return response.json();
  };

  const removeBotsFromDomain = async (domainId, botIds) => {
    const token = localStorage.getItem("token");
    const response = await fetch(API_ENDPOINTS.removeDomainBots(domainId), {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ botIds })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || "Failed to remove bots from domain");
    }

    return response.json();
  };

  const updateDomainRule = async (domainId, domainData) => {
    setSavingDomainRule(true);
    setDomainRulesError("");

    try {
      const token = localStorage.getItem("token");

      const currentDomain = domainRules.find((d) => d.id === domainId);
      if (currentDomain && currentDomain.domain !== domainData.domain) {
        const response = await fetch(API_ENDPOINTS.updateDomain(domainId), {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ domain: domainData.domain })
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || "Failed to update domain rule");
        }
      }

      if (currentDomain) {
        const currentBots = new Set(currentDomain.allowedBots || []);
        const newBots = new Set(domainData.allowedBots || []);

        const botsToAdd = [...newBots].filter(
          (botId) => !currentBots.has(botId)
        );
        const botsToRemove = [...currentBots].filter(
          (botId) => !newBots.has(botId)
        );

        if (botsToAdd.length > 0) {
          await addBotsToDomain(domainId, botsToAdd);
        }

        if (botsToRemove.length > 0) {
          await removeBotsFromDomain(domainId, botsToRemove);
        }
      }

      await fetchDomainRules();
      onSuccess("Domain rule updated successfully");
      setDomainRuleModal(null);
      setDomainRuleFormData({ domain: "", allowedBots: [] });
    } catch (error) {
      setDomainRulesError(error.message || "Network error. Please try again.");
    } finally {
      setSavingDomainRule(false);
    }
  };

  const deleteDomainRule = async (domainId) => {
    setDeletingDomainRules((prev) => new Set(prev).add(domainId));
    setDomainRulesError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.deleteDomain(domainId), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();

      if (response.ok) {
        setDomainRules((prev) =>
          prev.filter((domain) => domain.id !== domainId)
        );
        onSuccess(data.message || "Domain rule deleted successfully");
      } else {
        setDomainRulesError(data.error || "Failed to delete domain rule");
      }
    } catch (error) {
      setDomainRulesError("Network error. Please try again.");
    } finally {
      setDeletingDomainRules((prev) => {
        const newSet = new Set(prev);
        newSet.delete(domainId);
        return newSet;
      });
      setDomainRuleDeleteConfirmation(null);
    }
  };

  const handleCreateDomainRule = () => {
    setDomainRuleModal("create");
    setDomainRuleFormData({ domain: "", allowedBots: [] });
    setDomainRulesError("");
  };

  const handleEditDomainRule = (domainObj) => {
    setDomainRuleModal(domainObj);
    setDomainRuleFormData({
      domain: domainObj.domain,
      allowedBots: domainObj.allowedBots || []
    });
    setDomainRulesError("");
  };

  const handleSaveDomainRule = () => {
    if (!domainRuleFormData.domain.trim()) {
      setDomainRulesError("Domain is required");
      return;
    }

    const domainRegex =
      /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/;
    if (!domainRegex.test(domainRuleFormData.domain.trim())) {
      setDomainRulesError(
        "Please enter a valid domain name (e.g., example.com, example.co.us)"
      );
      return;
    }

    if (domainRuleModal === "create") {
      createDomainRule(domainRuleFormData);
    } else {
      updateDomainRule(domainRuleModal.id, domainRuleFormData);
    }
  };

  const handleCancelDomainRuleModal = () => {
    setDomainRuleModal(null);
    setDomainRuleFormData({ domain: "", allowedBots: [] });
    setDomainRulesError("");
  };

  const handleDomainRuleDeleteClick = (domain) => {
    setDomainRuleDeleteConfirmation(domain);
  };

  const handleDomainRuleDeleteConfirm = () => {
    if (domainRuleDeleteConfirmation) {
      deleteDomainRule(domainRuleDeleteConfirmation.id);
    }
  };

  const handleDomainRuleDeleteCancel = () => {
    setDomainRuleDeleteConfirmation(null);
  };

  const handleDomainRuleBotToggle = (botId) => {
    setDomainRuleFormData((prev) => {
      const newAllowedBots = prev.allowedBots.includes(botId)
        ? prev.allowedBots.filter((id) => id !== botId)
        : [...prev.allowedBots, botId];

      return {
        ...prev,
        allowedBots: newAllowedBots
      };
    });
  };

  useEffect(() => {
    fetchDomainRules();
  }, []);

  return (
    <>
      <Header>
        <div></div>
        <Button onClick={handleCreateDomainRule}>Create new rule</Button>
      </Header>

      {domainRulesError && <ErrorState>{domainRulesError}</ErrorState>}

      {domainRulesLoading && !domainRules.length ? (
        <LoadingState>Loading domain rules...</LoadingState>
      ) : domainRules.length === 0 ? (
        <EmptyState>
          <h4>No domain rules found</h4>
          <p>
            Create domain rules to manage bot access for different
            organizations.
          </p>
        </EmptyState>
      ) : (
        <Table columns="2fr 1fr 2fr 450px">
          <Row>
            <Column>Domain</Column>
            <Column>Allowed bots</Column>
            <Column>Created</Column>
            <ActionsColumn>Actions</ActionsColumn>
          </Row>

          {domainRules
            .sort((a, b) => {
              // Always put default domain first
              if (a.domain === "default") return -1;
              if (b.domain === "default") return 1;
              // Then sort by creation date (newest first)
              return new Date(b.createdAt) - new Date(a.createdAt);
            })
            .map((domain) => (
              <Row key={domain.id}>
                <Column data-label="Domain">{domain.domain}</Column>
                <Column data-label="Allowed Bots">
                  {domain.allowedBots && domain.allowedBots.length > 0 ? (
                    <span style={{ fontWeight: "500" }}>
                      {domain.allowedBots.length} of {availableBots.length}
                    </span>
                  ) : (
                    <span style={{ color: "#6c757d", fontStyle: "italic" }}>
                      0 of {availableBots.length}
                    </span>
                  )}
                </Column>
                <Column data-label="Created">
                  {formatDate(domain.createdAt)}
                </Column>
                <ActionsColumn data-label="Actions">
                  <Button
                    onClick={() => handleEditDomainRule(domain)}
                    disabled={deletingDomainRules.has(domain.id)}
                  >
                    Edit
                  </Button>
                  {domain.domain !== "default" && (
                    <>
                      <Button
                        onClick={(event) =>
                          navigate(
                            `/admin/chat-logs/${domain.domain}`,
                            {},
                            event
                          )
                        }
                        disabled={deletingDomainRules.has(domain.id)}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        View logs
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleDomainRuleDeleteClick(domain)}
                        disabled={deletingDomainRules.has(domain.id)}
                      >
                        {deletingDomainRules.has(domain.id)
                          ? "Deleting..."
                          : "Delete"}
                      </Button>
                    </>
                  )}
                </ActionsColumn>
              </Row>
            ))}
        </Table>
      )}

      {/* Domain Rule Management Modal */}
      <Modal isOpen={!!domainRuleModal} onClose={handleCancelDomainRuleModal}>
        <ModalTitle>
          {domainRuleModal === "create"
            ? "Create domain rule"
            : domainRuleModal?.domain === "default"
            ? "Edit default bots"
            : "Edit domain rule"}
        </ModalTitle>
        <ModalDescription>
          {domainRuleModal === "create"
            ? "Create a new domain rule to determine which bots are available to users from this domain"
            : domainRuleModal?.domain === "default"
            ? "Configure which bots are available to users who don't match any specific domain rules"
            : `Determine which bots are available to users with this domain`}
        </ModalDescription>

        {domainRulesError && <ErrorState>{domainRulesError}</ErrorState>}

        {domainRuleModal !== "create" &&
        domainRuleModal?.domain === "default" ? null : (
          <div style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "600"
              }}
            >
              Domain
            </label>
            <Input
              type="text"
              value={domainRuleFormData.domain}
              onChange={(e) =>
                setDomainRuleFormData((prev) => ({
                  ...prev,
                  domain: e.target.value
                }))
              }
              placeholder="example.com"
              disabled={savingDomainRule}
            />
          </div>
        )}

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "600"
            }}
          >
            Allowed bots
          </label>
          <BotList>
            {availableBots.map((bot) => (
              <BotItem
                key={bot.id}
                onClick={() => handleDomainRuleBotToggle(bot.id)}
              >
                <BotCheckbox
                  type="checkbox"
                  checked={domainRuleFormData.allowedBots.includes(bot.id)}
                  onChange={() => {}}
                  disabled={savingDomainRule}
                />
                <BotLabel>
                  <BotIcon>{getBotIcon(bot)}</BotIcon>
                  {bot.name}
                </BotLabel>
              </BotItem>
            ))}
          </BotList>
        </div>

        <ModalActions>
          <Button
            variant="secondary"
            onClick={handleCancelDomainRuleModal}
            disabled={savingDomainRule}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveDomainRule} disabled={savingDomainRule}>
            {savingDomainRule ? "Saving..." : "Save"}
          </Button>
        </ModalActions>
      </Modal>

      {/* Domain Rule Delete Confirmation Modal */}
      <Modal
        isOpen={!!domainRuleDeleteConfirmation}
        onClose={handleDomainRuleDeleteCancel}
      >
        <ModalTitle>
          Delete rule for {domainRuleDeleteConfirmation?.domain}?
        </ModalTitle>
        <ModalDescription>
          Are you sure you want to permanently delete this domain rule?
        </ModalDescription>
        <ModalActions>
          <Button variant="secondary" onClick={handleDomainRuleDeleteCancel}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDomainRuleDeleteConfirm}
            disabled={deletingDomainRules.has(domainRuleDeleteConfirmation?.id)}
          >
            {deletingDomainRules.has(domainRuleDeleteConfirmation?.id)
              ? "Deleting..."
              : "Delete"}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}

export default DomainRulesTab;
